/* import __COLOCATED_TEMPLATE__ from './bulk-actions.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type AdminAwayService from 'embercom/services/admin-away-service';
import type Session from 'embercom/services/session';
import type IntlService from 'embercom/services/intl';
import { type AwayStatusReason } from 'embercom/objects/inbox/away-reason';

interface Args {
  selectedTeammateIds: number[];
  totalCount: number;
  hasAllSelected: boolean;
  onToggleAll: () => void;
  onComplete: () => void;
}

interface Signature {
  Args: Args;
}

export default class BulkActions extends Component<Signature> {
  @service declare adminAwayService: AdminAwayService;
  @service declare session: Session;
  @service declare intl: IntlService;
  @service notificationsService: any;
  @service intercomEventService: any;

  @tracked isProcessing = false;

  get selectedCount() {
    return this.args.selectedTeammateIds.length;
  }

  get awayStatusReasons() {
    return this.adminAwayService.awayStatusReasons;
  }

  get canReassignConversationsOnAway() {
    if (!this.session.workspace.isFeatureEnabled('spg-inbox-reassign-conversations-on-away')) {
      return true;
    }
    return this.session.teammate?.permissions?.canReassignConversationsOnAway ?? false;
  }

  @action
  async setActive() {
    await this.performBulkAction(false, false, null);
  }

  @action
  async setAwayWithStatus(awayStatusReason: AwayStatusReason | null = null, reassign = false) {
    await this.performBulkAction(true, reassign, awayStatusReason);
  }

  private async performBulkAction(
    awayModeEnabled: boolean,
    reassign: boolean,
    awayStatusReason: AwayStatusReason | null,
  ) {
    this.isProcessing = true;
    try {
      await this.adminAwayService.bulkSetAwayMode(
        this.args.selectedTeammateIds,
        awayModeEnabled,
        reassign,
        awayStatusReason,
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('inbox.dashboard.teammate-activity.bulk-actions.success', {
          count: this.selectedCount,
        }),
      );
      this.trackBulkAction(awayModeEnabled, reassign);
      this.args.onComplete();
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('inbox.dashboard.teammate-activity.bulk-actions.error'),
      );
    } finally {
      this.isProcessing = false;
    }
  }

  private trackBulkAction(awayModeEnabled: boolean, reassign: boolean) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'bulk_change_away_mode',
      object: 'teammate_activity_table',
      place: 'monitoring',
      away_mode_enabled: awayModeEnabled,
      reassign_conversations: reassign,
      count: this.selectedCount,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Dashboard::Teammates::BulkActions': typeof BulkActions;
    'inbox2/dashboard/teammates/bulk-actions': typeof BulkActions;
  }
}
